import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { ModuleFilter } from "../../../utils/ModuleFilter";
import { TableToolbar } from "../../../utils/TableToolbar";
import { useTableView } from "../../../utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../utils/PMTableView/RightDrawer";
import {
  embeddedTableKey,
  mergeScopedFilters,
  scopeFiltersKey,
  computeHasMore,
} from "../../../utils/PMTableView/embedded-table-utils";
import { getDeviationsAsync } from "../../../../slices/quality/deviation";
import {
  DEVIATION_LIST_FIELDS,
  DeviationsDataTable,
  getDeviationColumns,
} from "./deviationsTableNewView";
import {
  DeviationPriority,
  DeviationSeverity,
  DeviationStatus,
  deviationStatusOptions,
  getDeviationTypeOptions,
  priorityOptions,
} from "./utils";
import { fetchUsers } from "../../../../api/user";

const FETCH_DEBOUNCE_MS = 250;

const DEFAULT_FILTER_STATE = {
  query: "",
  deviationStatuses: [],
  users: [],
  deviationTypes: [],
  priorities: [],
  onlyLinkedWorkOrders: false,
};

function buildBackendFilters(scopeFilters, filters) {
  const userFilters = [];

  if (filters.deviationStatuses?.length > 0) {
    userFilters.push({
      name: "status",
      value: JSON.stringify(filters.deviationStatuses),
    });
  }
  if (filters.users?.length > 0) {
    userFilters.push({
      name: "responsible_user",
      value: JSON.stringify(filters.users),
    });
  }
  if (filters.deviationTypes?.length > 0) {
    userFilters.push({
      name: "deviation_type",
      value: JSON.stringify(filters.deviationTypes),
    });
  }
  if (filters.priorities?.length > 0) {
    userFilters.push({
      name: "priority",
      value: JSON.stringify(filters.priorities),
    });
  }
  if (filters.onlyLinkedWorkOrders) {
    userFilters.push({ name: "work_order", value: true });
  }

  return mergeScopedFilters(scopeFilters, userFilters);
}

export const DeviationStacklist = (props) => {
  const { filters: scopeFilters = [], module: moduleName = "embedded" } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemState = useSelector((store) => store.deviations);
  const { isLoading, totalCount } = itemState;

  const loadMoreInFlightRef = useRef(false);

  const scopeKey = scopeFiltersKey(scopeFilters);

  const [controller, setController] = useState(() => ({
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    sort: props.sort || "desc",
    sortBy: props.sortBy || "updated_at",
    view: "all",
    fields: props.fields || DEVIATION_LIST_FIELDS,
  }));

  const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);
  const [userOptions, setUserOptions] = useState([]);
  const [deviationTypeOptions, setDeviationTypeOptions] = useState([]);
  const [drawerItem, setDrawerItem] = useState(null);

  useEffect(() => {
    fetchUsers({
      filters: [{ name: "role", value: "deviations" }],
      page: 1,
      per_page: 100,
      query: "",
      sort: "asc",
      sortBy: "name",
      view: "all",
    })
      .then((res) => {
        setUserOptions(
          (res.data || []).map((row) => ({
            value: String(row.id),
            label: row.displayTitle || row.name,
          }))
        );
      })
      .catch(console.error);

    getDeviationTypeOptions().then((options) => {
      setDeviationTypeOptions(
        (options || []).map((option) => ({
          value: String(option.value),
          label: option.title,
        }))
      );
    });
  }, []);

  const buildFetchController = useCallback(
    (page = controller.page, append = false) => ({
      filters: buildBackendFilters(scopeFilters, filters),
      page,
      per_page: controller.per_page,
      query: filters.query || "",
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: controller.view,
      fields: controller.fields,
      ...(append ? { append: true } : {}),
    }),
    [scopeFilters, filters, controller]
  );

  const fetchKey = useMemo(
    () =>
      JSON.stringify({
        scopeKey,
        filters,
        per_page: controller.per_page,
        sort: controller.sort,
        sortBy: controller.sortBy,
      }),
    [
      scopeKey,
      filters,
      controller.per_page,
      controller.sort,
      controller.sortBy,
    ]
  );

  const columns = useMemo(() => getDeviationColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: embeddedTableKey("deviations", moduleName),
    columns: columnMeta,
    getSnapshot: () => ({
      filters: buildBackendFilters(scopeFilters, filters),
      query: filters.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      const nextFilters = { ...DEFAULT_FILTER_STATE, query: filters.query };
      (snap.filters || []).forEach((filter) => {
        if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
        try {
          const values = JSON.parse(filter.value);
          switch (filter.name) {
            case "status":
              nextFilters.deviationStatuses = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "responsible_user":
              nextFilters.users = Array.isArray(values) ? values : [values];
              break;
            case "deviation_type":
              nextFilters.deviationTypes = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "priority":
              nextFilters.priorities = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            default:
              break;
          }
        } catch {
          if (filter.name === "work_order") {
            nextFilters.onlyLinkedWorkOrders = true;
          }
        }
      });
      setFilters((prev) => ({
        ...prev,
        ...nextFilters,
        query: snap.query ?? prev.query,
      }));
      setController((c) => ({
        ...c,
        page: 1,
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
  };

  const customDropdownFilters = useMemo(
    () => [
      {
        key: "deviationStatuses",
        label: t("Statuses"),
        options: deviationStatusOptions.map((status) => ({
          value: String(status.value),
          label: status.label,
        })),
      },
      {
        key: "deviationTypes",
        label: t("Deviation Types"),
        options: deviationTypeOptions,
      },
      {
        key: "priorities",
        label: t("Priorities"),
        options: priorityOptions.map((priority) => ({
          value: String(priority.value),
          label: priority.label,
        })),
      },
    ],
    [t, deviationTypeOptions]
  );

  const customCheckboxButtonFilters = useMemo(
    () => [
      {
        key: "onlyLinkedWorkOrders",
        label: t("Kun knyttet arbeidsordre"),
        defaultValue: false,
      },
    ],
    [t]
  );

  const handleLoadMore = useCallback(() => {
    const loaded = itemState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (controller.page || 1) + 1;
    loadMoreInFlightRef.current = true;
    dispatch(getDeviationsAsync(buildFetchController(nextPage, true))).finally(
      () => {
        loadMoreInFlightRef.current = false;
      }
    );
    setController((c) => ({ ...c, page: nextPage }));
  }, [
    controller.page,
    dispatch,
    buildFetchController,
    isLoading,
    itemState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () =>
      debounce((ctrl) => {
        dispatch(getDeviationsAsync(ctrl));
      }, FETCH_DEBOUNCE_MS),
    [dispatch]
  );

  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch(buildFetchController(1, false));
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = itemState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  return (
    <div className="min-w-0 space-y-4">
      <ModuleFilter
        filterCard
        disabled={isLoading}
        onFiltersChange={handleFiltersChange}
        initialFilters={filters}
        searchPlaceholder={t("Search deviations...")}
        filterTitle={t("Filter Deviations")}
        hideStatusFilter
        userOptions={userOptions}
        customDropdownFilters={customDropdownFilters}
        customCheckboxButtonFilters={customCheckboxButtonFilters}
      />

      <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
        <TableToolbar
          groupOptions={groupOptions}
          activeGroup={tableView.groupBy}
          onGroupChange={tableView.setGroupBy}
          columns={columnMeta}
          columnOrder={tableView.order}
          visibleKeys={tableView.visibleKeys}
          onToggleColumn={tableView.toggleColumn}
          onReorderColumn={tableView.reorderColumn}
          onResetColumns={tableView.resetColumns}
          savedViews={tableView.views}
          activeSavedViewId={tableView.activeViewId}
          savedViewDirty={tableView.dirty}
          onApplySavedView={tableView.applyView}
          onSaveAsView={tableView.saveAsView}
          onUpdateView={tableView.updateView}
          onDeleteView={tableView.deleteView}
          density={tableView.density}
          onDensityChange={tableView.setDensity}
        />
        <DeviationsDataTable
          columns={columns}
          visibleKeys={tableView.visibleKeys}
          widths={tableView.widths}
          onResize={tableView.resizeColumn}
          groupBy={tableView.groupBy}
          sort={tableSort}
          onSort={handleSort}
          itemState={itemState}
          density={tableView.density}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onRowClick={setDrawerItem}
        />
      </div>

      <RightDrawer
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        eyebrow={drawerItem ? `${t("Number")} ${drawerItem.number || ""}` : ""}
        title={drawerItem?.title || drawerItem?.displayTitle || t("Deviation")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(`/dashboard/hse-ks/deviations/${drawerItem.id}`),
          },
        ]}
      >
        {drawerItem && (
          <DrawerSection>
            <DrawerProps
              items={[
                {
                  label: t("Status"),
                  value: <DeviationStatus deviation={drawerItem} />,
                },
                {
                  label: t("Priority"),
                  value: <DeviationPriority deviation={drawerItem} />,
                },
                {
                  label: t("Severity"),
                  value: <DeviationSeverity deviation={drawerItem} />,
                },
                {
                  label: t("Type"),
                  value: drawerItem.deviation_type?.displayTitle || "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>
    </div>
  );
};

DeviationStacklist.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "updated_at",
};
